import { IAnswerDetails } from '@root/types';
import { IUserFormInput } from '../types';
import questions from '../data/questions';
import getRange from './getRange';

const getFinalScore = (answers: IUserFormInput) => {
  const detailedAnswers = getAnswersWithScoringDetails(answers);
  const finalScore = Math.round(calculateScore(detailedAnswers));

  return finalScore;
};

/**
 * Takes user provided formdata and appends it with data needed to calculate score.
 * @param answers The formData answers provided by form.
 */

export const getAnswersWithScoringDetails = (
  answers: IUserFormInput,
): IAnswerDetails[] => {
  const answerArray = Object.keys(answers).map(
    (answerId): IAnswerDetails => {
      const allQuestions = questions.flatMap((questionGroup) => {
        return questionGroup.questions;
      });
      const foundQuestion = allQuestions.find(
        (question) => question.id === answerId,
      );
      if (!foundQuestion) {
        throw new Error(
          `There was a problem with the question data structure, unable to find groupId for question with id ${answerId}`,
        );
      }
      const group = questions.find(
        (questionGroup) => questionGroup.groupId === foundQuestion.groupId,
      );

      if (!group) {
        throw new Error(
          `There was a problem with the question data structure, unable to find group for question with id ${answerId}`,
        );
      }

      const score = getQuestionScore(answers[answerId], foundQuestion.groupId);
      return {
        questionId: answerId,
        answer: answers[answerId],
        groupId: foundQuestion.groupId,
        title: foundQuestion.title,
        scored: group.scored,
        grid: group.grid,
        range: getRange(score),
        score,
      };
    },
  );
  return filterScoredAnswers(answerArray);
};

/**
 * Calculates total score.
 * @param answers User answers with extended information such as groupId and score value
 */

export const calculateScore = (answers: IAnswerDetails[]) => {
  let score = 0;
  answers.forEach((answer) => {
    const questionGroup = questions.find(
      (group) => group.groupId === answer.groupId,
    );
    if (questionGroup && questionGroup.grid) {
      const points = questionGroup.gridAlternatives.find(
        (alternative) => alternative.text === answer.answer,
      )?.score;
      if (points) score += points;
    }
  });
  return score / 12;
};

export const getQuestionScore = (answer: string, groupId: string): number => {
  const questionGroup = questions.find((group) => group.groupId === groupId);
  if (questionGroup && questionGroup.grid) {
    return (
      questionGroup.gridAlternatives.find(
        (alternative) => alternative.text === answer,
      )?.score || 0
    );
  }
  return 0;
};

const filterScoredAnswers = (answerArray: IAnswerDetails[]) => {
  return answerArray.filter((answer) => answer.grid);
};

export default getFinalScore;
